import React, { useState, useEffect, createContext, useCallback } from 'react';

import '../PlayerPage.css';
import NewComment from './NewComment';
import { useAtomValue } from 'jotai';
import { userInfoAtom } from '../../../data/atoms/utilsAtoms';
import { Dialog, DialogActions, DialogTitle } from '@mui/material';
import axios from 'axios';
import DisplayComment from './DisplayComment';
import { commentsStyles } from './style';
import { Transition } from './components/Transition';
import { useVideoDetailsFromParams } from '../hooks/useVideoDetailsFromParams';


export const CommentsContext = createContext();

function Comments() {
	const { videoId, target, typeOfContentView } = useVideoDetailsFromParams();

	const [totalComments, setTotalComments] = useState(0);
	const [cursorStamp, setCursorStamp] = useState(undefined);
	const [comments, setComments] = useState([]);
	const [replying, setReplying] = useState('');
	const [editing, setEditing] = useState('');

	const [deleteIsOpen, setDeleteIsOpen] = useState(false);
	const [deleteComment, setDeleteComment] = useState(false);
	const [moreIsLoading, setMoreIsLoading] = useState(false);
	const [commentState, setCommentState] = useState(false);
	const userData = useAtomValue(userInfoAtom);

	useEffect(() => {
		setComments([]);
		setCursorStamp(undefined);
		setTotalComments(0);
	}, [videoId]);

	const fetchComments = useCallback(async () => {
		if (!videoId) return;

		try {
			const response = await axios.get(
				`${import.meta.env.VITE_APP_API_URL}/comments?videoID=${videoId}${
					cursorStamp ? `&cursor=${cursorStamp}` : ''
				}${target ? `&target=${target}` : ''}&typeOfContentView=${typeOfContentView}`,
				{
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${userData.token}`
					}
				}
			);
			
			const data = response.data;
			

			setCommentState(data.commentState);

			setComments(prevComments => {
				if (!cursorStamp) {
					return data.comments || [];
				}
				const seenMap = new Set(prevComments.map(c => c._id));
				const updatedComments = [...prevComments];

				data.comments?.forEach(comment => {
					if (!seenMap.has(comment._id)) {
						seenMap.add(comment._id);
						updatedComments.push(comment);
					}
				});

				return updatedComments;
			});

			if (data.total) {
				setTotalComments(data.total);
			}

			if (data.comments && data.comments.length > 0) {
				setCursorStamp(data.comments[data.comments.length - 1].createdAt);
			}
		} catch (error) {
			console.error('Error fetching comments:', error);
		}
	}, [videoId, cursorStamp, userData.token]);

	useEffect(() => {
		fetchComments();
	}, [videoId]);

	return (
		<div className="c-video_comment">
			<div className="c-video_commet_we">
				<div className="c-video_comment-h4">
					<h4>Community Chat</h4>
					<div className={'c-video_comment-count'}>{totalComments}</div>
				</div>

				{comments.length === 0 && (
					<div className="c-video_comment-chat">
						<p className="c-community-chat">No comments have been made yet. Be the first to comment!</p>
					</div>
				)}

				<CommentsContext.Provider
					value={{
						replying,
						setReplying,
						comments,
						setComments,
						editing,
						setEditing,
						deleteIsOpen,
						setDeleteIsOpen,
						deleteComment,
						setDeleteComment,
						setTotalComments,
						commentState
					}}
				>
					<React.Fragment>
						<NewComment videoId={videoId} commentState={commentState} typeOfContentView={typeOfContentView} />

						<div className={'c-video_display-comments'}>
							{comments.map(comment => (
								<DisplayComment key={comment._id} comment={comment} />
							))}
						</div>
					</React.Fragment>
				</CommentsContext.Provider>

				{comments.reduce((acc, comment) => {
					return acc + comment.replies.length + 1;
				}, 0) < totalComments && (
					<>
						<div className={'c-video_comment-load-more-divider'} />

						<div
							className="c-video_comment-restor"
							onClick={() => {
								setMoreIsLoading(true);
								fetchComments();
							}}
						>
							<h4>
								<span>
									<i className="fas fa-sync"></i>
								</span>
								<span className={'c-video_comment-load-more'}>LOAD MORE COMMENTS</span>
							</h4>
						</div>
					</>
				)}
			</div>

			<Dialog
				open={deleteIsOpen}
				TransitionComponent={Transition}
				keepMounted
				onClose={() => {
					setDeleteComment(false);
					setDeleteIsOpen(false);
				}}
				sx={[commentsStyles.dialog]}
				aria-describedby="alert-dialog-slide-description"
			>
				<DialogTitle>{'Are you sure you want to delete this comment?'}</DialogTitle>
				<DialogActions>
					<button
						className={'c-video_comments-button-wrapper'}
						style={{
							width: 'max-content',
							height: 'max-content'
						}}
						onClick={() => {
							setDeleteComment(false);
							setDeleteIsOpen(false);
						}}
					>
						<div className={'c-video_comments-button-wrapper-inner'}>NO</div>
					</button>
					<button
						className={'c-video_comments-button-wrapper'}
						style={{
							width: 'max-content',
							height: 'max-content'
						}}
						onClick={() => {
							setDeleteComment(true);
							setDeleteIsOpen(false);
						}}
					>
						<div className={'c-video_comments-button-wrapper-inner'}>YES</div>
					</button>
				</DialogActions>
			</Dialog>
		</div>
	);
}

export default Comments;
